import 'react-datepicker/dist/react-datepicker.css'
import './DatePicker.css'
import { createContext, ReactNode, useContext, useEffect, useMemo, useRef } from 'react'
import ReactDatePicker, { ReactDatePickerProps } from 'react-datepicker'

import { cn } from '@sparkai/utils'

import { FormControl } from '../../FormControl'
import { Input } from '../../Input'
import { DatePickerHeader } from './DatePickerHeader'

type DatePickerCalendarContextValue = {
  isTimeInput?: boolean
  calendarHeader?: ReactNode
  timeHeaderAddon?: ReactNode
  timeFooter?: ReactNode
}

const DatePickerCalendarContext = createContext<DatePickerCalendarContextValue>({})
const DEFAULT_INTERACTIVE_EVENT_NAMES = ['mousedown']
const TIME_FOOTER_INTERACTIVE_EVENT_NAMES = ['mousedown', 'touchstart', 'click']

function DatePickerInteractiveContent({
  className,
  children,
  eventNames = DEFAULT_INTERACTIVE_EVENT_NAMES,
}: Readonly<{ className: string; children: ReactNode; eventNames?: string[] }>) {
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const content = contentRef.current
    const stopPropagation = (event: Event) => event.stopPropagation()

    eventNames.forEach((eventName) =>
      content?.addEventListener(eventName, stopPropagation, { capture: true })
    )
    return () => {
      eventNames.forEach((eventName) =>
        content?.removeEventListener(eventName, stopPropagation, { capture: true })
      )
    }
  }, [eventNames])

  return (
    <div ref={contentRef} className={className}>
      {children}
    </div>
  )
}

function DatePickerCalendarContainer({
  className,
  children,
}: Readonly<{
  className?: string
  children?: ReactNode | ReactNode[]
}>) {
  const { isTimeInput, calendarHeader, timeHeaderAddon, timeFooter } = useContext(DatePickerCalendarContext)
  return (
    <div className={cn(className, 'relative')}>
      {calendarHeader && (
        <DatePickerInteractiveContent className='react-datepicker__calendar-header-addon'>
          {calendarHeader}
        </DatePickerInteractiveContent>
      )}
      {children}
      {timeHeaderAddon && (
        <DatePickerInteractiveContent
          className={cn(
            'z-10',
            calendarHeader
              ? 'hidden lg:absolute lg:top-[116px] lg:right-6 lg:block'
              : 'hidden lg:absolute lg:top-6 lg:right-6 lg:block'
          )}
        >
          {timeHeaderAddon}
        </DatePickerInteractiveContent>
      )}
      {timeFooter && (
        <DatePickerInteractiveContent
          eventNames={TIME_FOOTER_INTERACTIVE_EVENT_NAMES}
          className={cn(
            'z-10 w-full',
            calendarHeader && 'mt-3 lg:absolute lg:right-6 lg:bottom-12 lg:mt-0 lg:w-[264px]',
            !calendarHeader && 'mt-4 lg:absolute lg:mt-0',
            !calendarHeader && isTimeInput && 'lg:right-6 lg:bottom-14 lg:w-[264px]',
            !calendarHeader && !isTimeInput && 'lg:right-6 lg:bottom-6 lg:w-[192px]'
          )}
        >
          {timeFooter}
        </DatePickerInteractiveContent>
      )}
    </div>
  )
}

export interface DateInterval {
  start: Date
  end: Date
}

export interface DatePickerProps {
  value?: Date | null
  inputValue?: string
  onChange: (date: Date | null) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  onKeyDown?: ReactDatePickerProps['onKeyDown']

  showTimeSelect?: boolean
  timeIntervals?: number
  monthsShown?: number
  showTimeInput?: boolean

  excludeDates?: Date[]
  excludeDateIntervals?: DateInterval[]
  filterDate?: (date: Date) => boolean

  excludeTimes?: Date[]
  filterTime?: (date: Date) => boolean

  shouldCloseOnSelect?: boolean
  showMonthAndYearSelector?: boolean
  timeFormat?: string
  dateFormat?: string

  maxDate?: Date | null
  minDate?: Date | null

  selectsRange?: boolean
  onSelect?: (date: Date) => void
  calendarHeaderAddon?: ReactNode
  calendarTimeFormat?: '12h' | '24h'
  calendarTimeHeaderAddon?: ReactNode
  calendarTimeFooter?: ReactNode
  popperContainer?: ReactDatePickerProps['popperContainer']
  popperPlacement?: ReactDatePickerProps['popperPlacement']
}

export function DatePicker({
  value,
  inputValue,
  showMonthAndYearSelector,
  placeholder,
  showTimeInput,
  showTimeSelect,
  className,
  calendarHeaderAddon,
  calendarTimeFormat,
  calendarTimeHeaderAddon,
  calendarTimeFooter,
  ...props
}: Readonly<DatePickerProps>) {
  const calendarContextValue = useMemo(
    () => ({
      isTimeInput: showTimeInput,
      calendarHeader: calendarHeaderAddon,
      timeHeaderAddon: calendarTimeHeaderAddon,
      timeFooter: calendarTimeFooter,
    }),
    [calendarHeaderAddon, calendarTimeHeaderAddon, calendarTimeFooter, showTimeInput]
  )

  return (
    <div className='root-defaultView'>
      <DatePickerCalendarContext.Provider value={calendarContextValue}>
        <ReactDatePicker
          selected={value}
          value={inputValue}
          customTimeInput={
            <CustomTimeInput
              key={calendarTimeFooter ? calendarTimeFormat : 'default'}
              timeFormat={calendarTimeFooter ? calendarTimeFormat : undefined}
              timeHeaderAddon={calendarTimeHeaderAddon}
            />
          }
          showPopperArrow={false}
          customInput={<Input className={className} />}
          calendarContainer={DatePickerCalendarContainer}
          calendarClassName={cn(
            'shadow-xl ring-1 ring-black/5 p-6 border-0! rounded-md',
            calendarHeaderAddon && 'react-datepicker--with-calendar-header',
            calendarTimeFooter && 'react-datepicker--with-timezone-footer pb-6',
            showTimeInput && 'react-datepicker--with-time-input',
            showTimeInput && !calendarTimeFooter && 'flex-col'
          )}
          renderCustomHeader={(props) => (
            <DatePickerHeader {...props} showMonthAndYearSelector={showMonthAndYearSelector ?? false} />
          )}
          placeholderText={placeholder}
          showTimeInput={showTimeInput}
          showTimeSelect={showTimeSelect}
          {...props}
        />
      </DatePickerCalendarContext.Provider>
    </div>
  )
}

type CustomTimeInputProps = Readonly<{
  value?: string
  onChange?: (value: string) => void
  timeFormat?: '12h' | '24h'
  timeHeaderAddon?: ReactNode
}>

type TimePeriod = 'AM' | 'PM'

function clampTimePart(value: number, min: number, max: number) {
  if (Number.isNaN(value)) return min
  return Math.min(Math.max(value, min), max)
}

function formatTimePart(value: number) {
  return String(value).padStart(2, '0')
}

function getTimeParts(value?: string) {
  const [rawHours = '0', rawMinutes = '0'] = value?.split(':') ?? []
  const hours = clampTimePart(Number(rawHours), 0, 23)
  const minutes = clampTimePart(Number(rawMinutes), 0, 59)
  const period: TimePeriod = hours >= 12 ? 'PM' : 'AM'
  const displayHours = hours % 12 || 12

  return { hours, minutes, period, displayHours }
}

function getHoursFromDisplay(displayHours: number, period: TimePeriod) {
  const normalizedHours = displayHours % 12
  return period === 'PM' ? normalizedHours + 12 : normalizedHours
}

function CustomTimeInput({ value, onChange, timeFormat, timeHeaderAddon }: CustomTimeInputProps) {
  const is12Hour = timeFormat === '12h'
  const { hours, minutes, period, displayHours } = getTimeParts(value)

  const applyTime = (nextHours: number, nextMinutes: number) => {
    onChange?.(`${formatTimePart(nextHours)}:${formatTimePart(nextMinutes)}`)
  }

  const handleHoursChange = (nextValue: string) => {
    const nextDisplayHours = clampTimePart(Number(nextValue), is12Hour ? 1 : 0, is12Hour ? 12 : 23)
    applyTime(is12Hour ? getHoursFromDisplay(nextDisplayHours, period) : nextDisplayHours, minutes)
  }

  const handleMinutesChange = (nextValue: string) => {
    applyTime(hours, clampTimePart(Number(nextValue), 0, 59))
  }

  const handlePeriodChange = (nextPeriod: TimePeriod) => {
    applyTime(getHoursFromDisplay(displayHours, nextPeriod), minutes)
  }

  return (
    <div className='w-[calc(100%-2.25rem)] lg:w-[224px]'>
      {timeHeaderAddon && (
        <DatePickerInteractiveContent className='mb-4 flex items-center justify-between lg:hidden'>
          <span className='text-base leading-6 font-semibold text-gray-900'>Time</span>
          {timeHeaderAddon}
        </DatePickerInteractiveContent>
      )}
      <div className='flex w-full items-start justify-between gap-4 pt-0 lg:w-[224px] lg:justify-start lg:gap-3'>
        <div>
          <span className='text-sm font-normal text-gray-500'>HR</span>
          <FormControl className='mt-3 w-[76px]'>
            <Input
              aria-label='Hours'
              type='number'
              className='datepicker-time-part-input'
              min={is12Hour ? 1 : 0}
              max={is12Hour ? 12 : 23}
              value={is12Hour ? displayHours : formatTimePart(hours)}
              onChange={(event) => handleHoursChange(event.target.value)}
            />
          </FormControl>
        </div>
        <span className='mt-[39px] w-1 text-xl font-semibold text-gray-700'>:</span>
        <div>
          <span className='text-sm font-normal text-gray-500'>MIN</span>
          <FormControl className='mt-3 w-[76px]'>
            <Input
              aria-label='Minutes'
              type='number'
              className='datepicker-time-part-input'
              min={0}
              max={59}
              value={formatTimePart(minutes)}
              onChange={(event) => handleMinutesChange(event.target.value)}
            />
          </FormControl>
        </div>
        {is12Hour && (
          <div className='mt-1 ml-1 flex h-[70px] w-9 shrink-0 flex-col rounded-md bg-gray-100'>
            {(['AM', 'PM'] as const).map((option) => (
              <button
                key={option}
                type='button'
                aria-pressed={period === option}
                className={cn(
                  'flex w-full flex-1 items-center justify-center rounded-md text-sm font-medium text-gray-500',
                  period === option && 'bg-white text-gray-900 shadow'
                )}
                onClick={() => handlePeriodChange(option)}
              >
                {option}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
